import React from 'react';

const FinanceiroTabela = ({ 
  titulo, 
  dados, 
  colunas, 
  className = '',
  notaRodape = '',
  compacto = false,
  altura = 'h-80'
}) => {
  // Verificar se os dados são válidos
  if (!Array.isArray(dados) || dados.length === 0) {
    return (
      <div className={`overflow-hidden rounded-lg border border-[color:var(--border)] bg-[color:var(--surface)] ${className} ${altura}`}>
        {titulo && (
          <div className="px-5 pt-4 pb-3 border-b border-[color:var(--border)]">
            <h3 className="text-base font-semibold text-[color:var(--text-primary)]">{titulo}</h3>
          </div>
        )}
        <div className="px-5 py-8 text-center text-[color:var(--text-muted)]">
          Nenhum dado disponível
        </div>
      </div>
    );
  }

  const renderizarCelula = (valor, coluna, linha) => {
    // Verificar se o valor é válido
    if (valor === null || valor === undefined) {
      return '';
    }

    // Verificar se a coluna é válida
    if (!coluna || typeof coluna !== 'object') {
      return String(valor || '');
    }

    // Renderizar campo de status com cores
    if (coluna.chave === 'status' && linha && linha.statusCor && linha.statusBg) {
      return (
        <span className={`px-2 py-1 rounded-full text-xs font-medium ${linha.statusBg} ${linha.statusCor}`}>
          {valor || ''}
        </span>
      );
    }
    
    // Renderizar campo de percentual
    if (coluna.formato === 'percentual' && valor !== null && valor !== undefined) {
      const numValor = parseFloat(valor);
      if (isNaN(numValor)) return '';
      return `${numValor.toFixed(1)}%`;
    }
    
    // Renderizar campo de moeda
    if (coluna.formato === 'moeda' && valor !== null && valor !== undefined) {
      const numValor = parseFloat(valor);
      if (isNaN(numValor)) return '';
      return numValor.toLocaleString('pt-BR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
    }
    
    // Renderizar valor padrão
    return String(valor || '');
  };

  // Verificar se as colunas são válidas
  if (!Array.isArray(colunas) || colunas.length === 0) {
    return (
      <div className={`overflow-hidden rounded-lg border border-[color:var(--border)] bg-[color:var(--surface)] ${className} ${altura}`}>
        {titulo && (
          <div className="px-5 pt-4 pb-3 border-b border-[color:var(--border)]">
            <h3 className="text-base font-semibold text-[color:var(--text-primary)]">{titulo}</h3>
          </div>
        )}
        <div className="px-5 py-8 text-center text-[color:var(--text-muted)]">
          Configuração de colunas inválida
        </div>
      </div>
    );
  }

  return (
    <div className={`flex flex-col overflow-hidden rounded-lg border border-[color:var(--border)] bg-[color:var(--surface)] ${className} ${altura}`}>
      {titulo && (
        <div className="px-5 pt-4 pb-3 border-b border-[color:var(--border)] flex-shrink-0">
          <h3 className="text-base font-semibold text-[color:var(--text-primary)]">{titulo}</h3>
        </div>
      )}
      
      <div className="flex-1 overflow-hidden">
        <div className="h-full overflow-y-auto overflow-x-hidden">
          <table className="w-full table-fixed border-separate border-spacing-0">
            <thead className="sticky top-0 z-20">
              <tr className="border-b border-[color:var(--border)]">
                {colunas.map((coluna, index) => {
                  // Verificar se a coluna é válida
                  if (!coluna || typeof coluna !== 'object' || !coluna.titulo) {
                    return <th key={index} className="sticky top-0 bg-[color:var(--surface)]/95 px-3 py-3 text-xs font-medium text-[color:var(--text-muted)] backdrop-blur supports-[backdrop-filter]:bg-[color:var(--surface)]/85">-</th>;
                  }

                  return (
                    <th 
                      key={index} 
                      className={`
                        sticky top-0 bg-[color:var(--surface)]/95 px-2 py-2.5 text-[10px] font-medium uppercase tracking-[0.08em] text-[color:var(--text-muted)] backdrop-blur supports-[backdrop-filter]:bg-[color:var(--surface)]/85 align-middle
                        ${coluna.alinhamento === 'right' ? 'text-right' : ''}
                        ${compacto ? 'py-1' : ''}
                        ${coluna.largura ? '' : ''}
                      `}
                      style={coluna.largura ? { width: coluna.largura } : undefined}
                    >
                      <span className="block w-full whitespace-normal break-words leading-tight">
                        {coluna.titulo}
                      </span>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody className="divide-y divide-[color:var(--border)]">
              {dados.map((linha, linhaIndex) => {
                // Verificar se a linha é válida
                if (!linha || typeof linha !== 'object') {
                  return null;
                }

                // Verificar se é um separador
                if (linha.isSeparator) {
                  return (
                    <tr key={linhaIndex} className="border-t-2 border-[color:var(--border)]">
                      <td colSpan={colunas.length} className="px-3 py-1"></td>
                    </tr>
                  );
                }

                // Verificar se a linha tem pelo menos uma das propriedades necessárias
                // Para DRE: descricao e valor
                // Para análise por cidade: cidade, valor_total, etc.
                const temPropriedadesDRE = linha.hasOwnProperty('descricao') && linha.hasOwnProperty('valor');
                const temPropriedadesCidade = linha.hasOwnProperty('cidade') && linha.hasOwnProperty('valor_total');
                
                if (!temPropriedadesDRE && !temPropriedadesCidade) {
                  return null;
                }

                // Determinar classe de fundo
                let bgClass = '';
                if (linha.isTotal && linha.statusBg) {
                  bgClass = linha.statusBg;
                } else if (linha.destaque) {
                  bgClass = 'bg-[color:var(--surface-muted)]';
                }

                return (
                  <tr 
                    key={linhaIndex}
                    className={`
                      ${bgClass}
                      ${linhaIndex % 2 === 0 && !linha.destaque && !linha.isTotal ? 'bg-[color:var(--surface)]' : ''}
                      ${linhaIndex % 2 === 1 && !linha.destaque && !linha.isTotal ? 'bg-[color:var(--surface-muted)]/20' : ''}
                      ${!linha.isTotal ? 'hover:bg-[color:var(--surface-muted)]/60 transition-colors' : ''}
                      ${linha.isTotal ? 'bg-emerald-500/10 font-bold' : ''}
                    `}
                  >
                    {colunas.map((coluna, colIndex) => {
                      // Verificar se a coluna é válida
                      if (!coluna || typeof coluna !== 'object' || !coluna.chave) {
                        return <td key={colIndex} className="px-3 py-2 text-xs text-[color:var(--text-muted)]">-</td>;
                      }

                      // Verificar se a linha tem a propriedade necessária
                      const valor = linha[coluna.chave];
                      // Para cabeçalhos e separadores, permitir valores null/undefined
                      if (valor === null || valor === undefined) {
                        // Se for um cabeçalho (destaque sem valor), renderizar descrição apenas na coluna de descrição
                        if (linha.destaque && !linha.isTotal && !linha.isSeparator && coluna.chave === 'descricao') {
                          return (
                            <td 
                              key={colIndex}
                              className={`
                                px-3 py-2 text-xs
                                ${coluna.alinhamento === 'right' ? 'text-right' : ''}
                                font-medium
                                ${linha.cor || 'text-[color:var(--text-primary)]'}
                                ${compacto ? 'py-1' : ''}
                              `}
                            >
                              {linha.descricao || ''}
                            </td>
                          );
                        }
                        // Para outras colunas ou separadores, renderizar vazio
                        return <td key={colIndex} className="px-3 py-2 text-xs"></td>;
                      }

                      // Verificar se o valor é um objeto (não deve ser renderizado diretamente)
                      if (typeof valor === 'object' && valor !== null) {
                        console.warn('Tentativa de renderizar objeto:', valor, 'na coluna:', coluna.chave, 'linha:', linha);
                        return <td key={colIndex} className="px-3 py-2 text-xs text-[color:var(--text-muted)]">-</td>;
                      }

                      // Verificar se o valor é uma string vazia ou apenas espaços
                      if (typeof valor === 'string' && valor.trim() === '') {
                        return <td key={colIndex} className="px-3 py-2 text-xs text-[color:var(--text-muted)]">-</td>;
                      }

                      return (
                        <td 
                          key={colIndex}
                          className={`
                            px-3 py-2 text-xs overflow-hidden text-ellipsis
                            ${coluna.alinhamento === 'right' ? 'text-right' : ''}
                            ${linha.isTotal ? 'font-bold' : 'font-medium'}
                            ${linha.cor || linha.statusCor || 'text-[color:var(--text-primary)]'}
                            ${compacto ? 'py-1' : ''}
                          `}
                          style={coluna.largura ? { width: coluna.largura } : undefined}
                        >
                          {renderizarCelula(valor, coluna, linha)}
                        </td>
                      );
                    })}
                  </tr>
                );
              }).filter(Boolean)}
            </tbody>
          </table>
        </div>
      </div>
      
      {notaRodape && (
        <div className="px-5 py-3 border-t border-[color:var(--border)] bg-[color:var(--surface-muted)] flex-shrink-0">
          <p className="text-xs text-[color:var(--text-muted)]">{notaRodape}</p>
        </div>
      )}
    </div>
  );
};

export default FinanceiroTabela;
